// eslint-disable-next-line import/no-unresolved -- CRA's import resolver doesn't read the package's `exports` map
import { createReduxEnhancer } from "@hyly/observability/react";

// Sentry's createReduxEnhancer is a raw @sentry/react passthrough, and the package's privacy
// scrub does NOT touch contexts.state or the redux_state.json attachment. So THIS FILE is the
// single control point for what Redux data ever leaves the app. Keep it a strict WHITELIST:
// never send secrets (api_key/mlApiKey), session ids (appSSID), or PII (chat text, email/phone,
// unbounded payload bags like scheduleTour.allData / standalone.params).

// Recent action SEQUENCE only — drop payloads (they carry email / entry_id / chat text).
const observabilityActionTransformer = (action) => (action?.type ? { type: action.type } : null);

// Curated whitelist — ONLY non-sensitive UI/flow fields. Anything not listed here is dropped,
// so new slice fields are excluded by default (safe by construction).
const observabilityStateTransformer = (state) => ({
  screen: {
    calculator: state?.calculator?.isCalculatorScreen,
    unitTour: state?.units?.isUnitTourScreen,
    photoTour: state?.gallery?.isPhotoTourScreen,
    videoTour: state?.videos?.isVideoTourScreen,
  },
  window: {
    isOpen: state?.window?.isOpen,
    isMaximized: state?.window?.isMaximized,
    isMobileWindow: state?.window?.isMobileWindow,
    isParentView: state?.window?.isParentView,
    conversationStopped: state?.window?.conversationStopped,
    conversationID: state?.window?.conversationID,
  },
  property: {
    org_id: state?.property?.org_id,
    property_id: state?.property?.property_id,
    property_name: state?.property?.property_name,
    entry_id: state?.property?.entry_id,
  },
  search: {
    bedroom_type: state?.params?.bedroom_type,
    price: state?.params?.price,
    desiredMoveIn: state?.params?.desiredMoveIn,
  },
  tour: {
    selectedOptions: state?.scheduleTour?.selectedOptions,
    tourAction: state?.scheduleTour?.tourAction,
  },
  calculator: {
    unitId: state?.calculator?.unitId,
    engrainId: state?.calculator?.engrainId,
  },
  // Which conversational flow / interactive widget was active at crash time —
  // the action log can't tell us this.
  flow: {
    module: state?.params?.module,
    activeOptionComp: state?.optionBox?.activeOptionComp,
  },
  // In-flight / async signals — catch race and mid-request crashes.
  async: {
    transStatus: state?.messages?.transStatus,
    tourLoading: state?.scheduleTour?.loading,
    faqLoader: state?.messages?.faqLoader,
  },
  // Conversation depth + FAQ mode. COUNT ONLY — never the chat text.
  messages: {
    count: state?.messages?.messages?.length,
    isFaq: state?.messages?.isFaq,
    page: state?.messages?.pagination?.page,
    loadMore: state?.messages?.pagination?.loadMore,
  },
});

/**
 * Build the Sentry Redux enhancer with strict PII/secret controls.
 * Attach it to the store ONLY when observability is enabled (production).
 */
export function buildReduxEnhancer() {
  return createReduxEnhancer({
    attachReduxState: false, // no unscrubbable redux_state.json attachment
    actionTransformer: observabilityActionTransformer,
    stateTransformer: observabilityStateTransformer,
  });
}
